// src/App.jsx - FIXED
import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Snackbar, Alert, CircularProgress, Box } from '@mui/material';
import { AppProvider, useAppContext } from './context/AppContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import { OnboardingFlow, useOnboarding } from './components/common/OnboardingFlow';
import theme from './styles/theme';
import Header from './components/common/Header';
import Footer from './components/common/Footer';

// Route-level code splitting — each page is a separate chunk.
// Header, Footer, and ErrorBoundary remain eager (always needed).
const HomePage        = lazy(() => import('./pages/HomePage'));
const AnalysisPage    = lazy(() => import('./pages/AnalysisPage'));
const ResultsPage     = lazy(() => import('./pages/ResultsPage'));
const ResearchPage    = lazy(() => import('./pages/ResearchPage'));
const ComparisonPage  = lazy(() => import('./pages/ComparisonPage'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage'));
const SponsorshipPage = lazy(() => import('./pages/SponsorshipPage'));
const BookDemoPage    = lazy(() => import('./pages/BookDemoPage'));
const TechnicalConsultationPage = lazy(() => import('./pages/TechnicalConsultationPage'));

const PageFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

const GlobalToast = () => {
  const { state, dispatch } = useAppContext();
  const handleClose = () => dispatch({ type: 'CLEAR_TOAST' });
  return (
    <Snackbar
      open={!!state.toast}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      {state.toast ? (
        <Alert onClose={handleClose} severity={state.toast.severity} variant="filled">
          {state.toast.message}
        </Alert>
      ) : <span />}
    </Snackbar>
  );
};

// Component to handle onboarding within the app context
const AppWithOnboarding = () => {
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const { shouldShow } = useOnboarding();

  // Show onboarding for new users after a short delay
  useEffect(() => {
    if (shouldShow) {
      const timer = setTimeout(() => {
        setOnboardingOpen(true);
      }, 2000); // 2 second delay to let the app load

      return () => clearTimeout(timer);
    }
  }, [shouldShow]);

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header />
        <main style={{ flex: 1, paddingTop: '64px' }}>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/analysis" element={<AnalysisPage />} />
              <Route path="/results/:id" element={<ResultsPage />} />
              <Route path="/results" element={<ResultsPage />} />
              <Route path="/research" element={<ResearchPage />} />
              <Route path="/comparison" element={<ComparisonPage />} />
              <Route path="/documentation" element={<DocumentationPage />} />
              <Route path="/sponsorship" element={<SponsorshipPage />} />
              <Route path="/book-demo" element={<BookDemoPage />} />
              <Route path="/technical-consultation" element={<TechnicalConsultationPage />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
      
      <GlobalToast />
      
      {/* Onboarding Flow */}
      <OnboardingFlow 
        open={onboardingOpen} 
        onClose={() => setOnboardingOpen(false)} 
      />
    </>
  );
};

const App = () => {
  return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <AppProvider>
            <Router>
              <AppWithOnboarding />
            </Router>
          </AppProvider>
        </ErrorBoundary>
      </ThemeProvider>
  );
};

export default App;